import React from "react";
import styled from "styled-components";

import Grid from "src/library/components/Hedron";
import { InstalledModuleType } from "src/utils/constants";
import { getOrderMode } from "src/core/drops/utils/inherited-settings";
import {
  IProduct,
  ProductStatus
} from "src/core/products/api/products.api";
import { IRole } from "src/core/shared/api/roles.api";
import { IOrderModeSetting } from "src/core/drops/api/drops.api";
import { IProductMaybeWithSubstitution } from "src/core/products/utils/filter-product-substitutions";
import useProductIsOrderable from "src/core/products/hooks/useProductIsOrderable";
import { useDrop } from "src/core/drops/hooks";
import { canListOrderProduct } from "src/core/orders/utils/orders";
import { MenuContext } from "src/core/shared/hooks/menu/useMenu";

import { IQuantityProps } from "./ProductQuantityButton";
import { ProductOrderingControls } from "./ProductOrderingControls";
import { useProductIsAllowedSubs } from "../../hooks/useProductIsAllowedSubs";

interface ProductActionsProps extends Partial<IQuantityProps> {
  product: IProductMaybeWithSubstitution;
  viewContext: "ordering" | "browsing";
  CreateOrderButton?: (product: IProduct) => React.ReactNode;
  currentRole: IRole | null;
  autoFocus?: boolean;
  installedModules: InstalledModuleType[];
  originalProduct?: IProduct;
}

export const ProductActions: React.ReactFCWithChildren<ProductActionsProps> = ({
  product,
  viewContext,
  CreateOrderButton,
  originalProduct
}) => {
  let orderMode: IOrderModeSetting | undefined;
  const [ { context } ] = React.useContext(MenuContext);
  const [ { drop } ] = useDrop();

  //are we currently  creating a list or an order?
  const productMessageContext: "list"|"order" = React.useMemo(() => {
    //some cases context is undefined
    if (context[ 0 ]?.type === "LIST_PRODUCT_SELECTION") {
      return "list";
    } else return "order";
  }, [ context ]);

  if (drop) {
    orderMode = getOrderMode(drop.groupedSettings);
  }

  // Get substituted product if exists
  const substitution = React.useMemo(() => product.substitution, [ product ]);
  const allowedSubs = useProductIsAllowedSubs(product);
  const unavailable = allowedSubs && product.substitution?.message || null;
  const orderable = useProductIsOrderable(originalProduct || product, unavailable);

  const lineMessage = React.useMemo(() => {
    let lineMessage = "";
    const productCurrentStatus = product.currentStatus?.status;

    if (productMessageContext !== "list") {
      if (product.madeToOrder && (!drop || (orderMode && orderMode.allow !== "MTO"))) {
        lineMessage = "Made to order products cannot be selected when a delivery date has been chosen";
      } else if (productCurrentStatus === ProductStatus.Suspended || productCurrentStatus === ProductStatus.Delisted) {
        lineMessage = `This product has been ${productCurrentStatus}`;
      } else if (substitution && !allowedSubs) {
        lineMessage = substitution.message;
      } else if (unavailable) {
        lineMessage = unavailable;
      } else if (!lineMessage && !orderable) {
        lineMessage = "This product is not available to order right now";
      }
    } else {
      //better messaging for a list
      lineMessage = canListOrderProduct(product) ?? "";
    }

    return lineMessage;
  }, [
    product,
    productMessageContext,
    drop,
    orderMode,
    substitution,
    allowedSubs,
    unavailable,
    orderable
  ]);

  return (
    <ProductActionsWrapper
      className="product-browsing product-card-actions"
      direction="horizontal"
      valign="center"
      allowWrap={viewContext === "browsing"}
    >
      <ProductOrderingControls
        product={product}
        context={viewContext === "browsing" ? "view" : "edit"}
        CreateOrderButton={CreateOrderButton}
        lineMessage={lineMessage}
      />

    </ProductActionsWrapper>
  );
};

const ProductActionsWrapper = styled(Grid.Bounds)`
  justify-content: space-between;

  .product-unavailable {
    position: absolute;
    top: 10px;
    right: 10px;
  }

  .add-product-wrapper .quantity-controls {
    flex-direction: row;
  }

  .quantity-controls {
    flex-direction: column;
  }
`;
